import React, {useCallback} from 'react';

import {Image} from '../../generated/types';
import {useGalleryContext} from '../gallery';
import type {GalleryImage} from '../gallery';
import {GalleryImageToolbar} from '../gallery/gallery-image-toolbar';
import type {GalleryTarget} from '../gallery/gallery-upload';
import {useGalleryAdmin} from '../gallery/use-gallery-admin';
import {useUser} from '../use-user';
import {ImageSize, imageMaxHeight, imageUrl} from '../utils';

import css from './main-image.module.css';

type Props = {
	image: Image;
	reserveAlt?: string;
	size?: ImageSize;
	/** к какой сущности относится картинка: администратору на ней показываются кнопки правки */
	target?: GalleryTarget;
};

/** Главная картинка — первая в галерее: её можно изменить, убрать или сдвинуть вторым номером */
const MainImageTools: React.FC<{image: Image; target: GalleryTarget; images: GalleryImage[]}> = ({image, target, images}) => {
	const {error, shift, edit, requestDelete, dialogs} = useGalleryAdmin(target, images);
	const current = images.find((item) => item.id === image.id);

	return (
		<>
			{dialogs}
			{error && <div className={css.error}>{error}</div>}
			<GalleryImageToolbar
				className={css.tools}
				onMoveRight={images.length > 1 ? () => shift(image.id, 1) : undefined}
				onEdit={() => current && edit(current)}
				onDelete={() => requestDelete(image.id)}
			/>
		</>
	);
};

function shownSize(image: Image, size: ImageSize): {width: number; height: number} | undefined {
	if (!image.width || !image.height) return undefined;
	const scale = Number.isFinite(size)
		? Math.min(1, size / Math.max(image.width, image.height))
		: Math.min(1, imageMaxHeight(size) / image.height);
	return {width: Math.round(image.width * scale), height: Math.round(image.height * scale)};
}

export const MainImage: React.FC<Props> = ({image, reserveAlt, size = 400, target}) => {
	const gallery = useGalleryContext();
	const {isAdmin} = useUser();
	const shown = shownSize(image, size);
	const inGallery = gallery?.images.some((img) => img.id === image.id) ?? false;

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			if (!inGallery) return;
			e.preventDefault();
			gallery!.openImage(image.id);
		},
		[inGallery, gallery, image.id],
	);

	return (
		<div className={css.wrapper}>
			<figure>
				<a href={imageUrl(image.path)} onClick={handleClick} className={inGallery ? css.clickable : undefined}>
					<img
						src={imageUrl(image.path, size)}
						alt={image.description || reserveAlt || ''}
						fetchPriority="high"
						width={shown?.width}
						height={shown?.height}
						style={{maxHeight: imageMaxHeight(size), aspectRatio: shown ? `${shown.width} / ${shown.height}` : undefined}}
					/>
				</a>
				{image.description && <figcaption>{image.description}</figcaption>}
			</figure>
			{isAdmin && target && inGallery && gallery && (
				<MainImageTools image={image} target={target} images={gallery.images} />
			)}
		</div>
	);
};
